import {
  removeReportFromIndexedDB,
  storeReportOnIndexedDB,
} from '@components/Header/ReportsDrawer/ReportsDrawer.utils';
import { AnalyticsAPI } from '@core/api';
import { Reports } from '@core/models/reports/reports.models';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

interface RetryBulkXlsxInput {
  report: Reports.ReportQueryData;
  workspaceId: string;
}

export function useRetryBulkXlsxReport() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['retry-bulk-xlsx-report'],
    mutationFn: async ({ report, workspaceId }: RetryBulkXlsxInput) => {
      const { data } = await AnalyticsAPI.post<Reports.BulkXlsxResponseDTO>(
        `/reports/${report.report_id}/retry`
      );

      const reportWorkspaceId = report.workspace_id ?? workspaceId;
      await removeReportFromIndexedDB(report, reportWorkspaceId);

      const retried: Reports.ReportQueryData = {
        type: report.type,
        status: Reports.ReportStatus.GENERATING,
        file_url: '',
        generated_at: new Date().toISOString(),
        report_id: data.key,
        is_new: true,
        workspace_id: reportWorkspaceId,
      };

      await storeReportOnIndexedDB(retried, reportWorkspaceId);

      return retried;
    },
    onSuccess: (_, { report }) => {
      queryClient.removeQueries({ queryKey: ['bulkXlsxStatus', report.report_id] });
      queryClient.invalidateQueries({ queryKey: ['bulkXlsxStoredReports'] });
      message.info(t('header.reports.messages.generating'), 2.5);
    },
    onError: () => {
      message.error(t('header.reports.messages.error'), 2.5);
    },
  });
}
